import React, { useCallback } from "react";
import "./ProductProperties.scss";
import { Collapse, List, Flex } from "antd";
import { useUser } from "contexts/UserContext";
import { useUnit } from "contexts/UnitContext";
import Helpers from "@utils/Helpers";

interface IProductProperties {
  data: any;
  deliveryTime?: number | null;
  distance?: number | null;
  type: "My Product" | "Product Detail";
}

const ProductProperties: React.FC<IProductProperties> = ({
  data,
  deliveryTime,
  distance,
  type,
}) => {
  const { userProfile, userAddresses } = useUser();
  const { units, convertDistance } = useUnit();

  const getUserAddress = useCallback(() => {
    if (!userProfile?.address_id) return;
    const address = userAddresses.find(
      (item: any) => item.address_id === userProfile?.address_id
    );
    return address;
  }, [userProfile?.address_id]);

  const productShipping = [
    {
      title: "Current Item Location",
      value: `${data?.city}, ${data?.province}`,
    },
    {
      title: "Delivery Postal Code",
      value: `${
        userProfile ? getUserAddress()?.postal_code : "Please log in first"
      }`,
    },
    {
      title: "Distance",
      value: `${Helpers.formatNumber(
        convertDistance(distance || 0 / 1000),
        0
      )} ${units.distance}`,
    },
    {
      title: "Approximate Driving Time",
      value: Helpers.convertSecondsToHoursAndMinutes(deliveryTime || 0),
    },
    {
      title: "Price",
      value: `$ 
      ${Helpers.formatNumber(4.35 * (distance || 0), 2)}`,
    },
  ];

  const productProperties = [
    { title: "Hay Type", value: data?.hay_type },
    { title: "Hay Cut", value: data?.hay_cut },
    { title: "Storage", value: data?.storage },
    { title: "Quantity", value: `${data?.quantity} Bales` },
    {
      title: "Bale Weight",
      value: `${(data?.bale_weight / 1000).toFixed(4)} Ton`,
    },
    { title: "Type of Binding", value: data?.type_of_binding },
    {
      title: "Size",
      value:
        data?.shape === "ssquare"
          ? '18" x 14" x 36"'
          : data?.shape === "lsquare"
          ? "4' x 4' x 8'"
          : data?.shape === "round"
          ? "5' x 6'"
          : "Unknown",
    },
    {
      title: "Hay Compression (Bailing)",
      value:
        data?.hay_compression &&
        data?.hay_compression.charAt(0).toUpperCase() +
          data?.hay_compression.slice(1),
    },
    {
      title: "Lab Verified",
      value: data?.crude_protein_range_id == 0 ? "No" : "Yes",
    },
    {
      title: "Shape",
      value:
        data?.shape === "ssquare"
          ? "Small Square"
          : data?.shape === "lsquare"
          ? "Large Square"
          : data?.shape === "round"
          ? "Round"
          : "Unknown",
    },
    { title: "Crop", value: data?.crop },
    { title: "Rain", value: data?.rain ? "Yes" : "No" },
    { title: "Moisture", value: `${data?.moisture}%` },
  ];

  return (
    <div className="product-props">
      <Collapse
        className="mb-1"
        collapsible="icon"
        defaultActiveKey={[type == "Product Detail" ? "1" : ""]}
        expandIconPosition={"end"}
        items={[
          {
            key: "1",
            label: "Hay Information",
            children: (
              <List
                dataSource={productProperties}
                renderItem={(item) => (
                  <List.Item>
                    <Flex flex={1} justify="space-between">
                      <div className="properties-title">{item.title}</div>
                      <div className="properties-value">{item.value}</div>
                    </Flex>
                  </List.Item>
                )}
              />
            ),
          },
        ]}
      />
      <Collapse
        collapsible="icon"
        defaultActiveKey={[type == "Product Detail" ? "2" : ""]}
        expandIconPosition={"end"}
        items={[
          {
            key: "2",
            label: "Shipping",
            children: (
              <List
                dataSource={productShipping}
                renderItem={(item) => (
                  <List.Item>
                    <Flex flex={1} justify="space-between">
                      <div className="properties-title">{item.title}</div>
                      <div className="properties-value">{item.value}</div>
                    </Flex>
                  </List.Item>
                )}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default ProductProperties;
